import React from 'react'

import Bus from '../../bus'
import BetManager from '../../managers/bet'

import { logger } from '../../utils'

import { emptyGlobalBetslipState } from './betslip.constants'

import { OddsHighlighter, useReportsOddsHighlight } from './odds.highlighter'
import { BET_INTERCEPT_STATUSES, BET_STATUSES, REJECTION_REASON } from '../../constants'
import { isEmpty } from 'lodash'

const GlobalBetslipContext = React.createContext()

export const GlobalBetslipActions = {
  BETSLIP_STATE_UPDATE: 'BETSLIP_STATE_UPDATE',
  BETSLIP_CLEAR_REPORTS: 'BETSLIP_CLEAR_REPORTS',
  BETSLIP_ADD_REPORT: 'BETSLIP_ADD_REPORT',
  BETSLIP_REMOVE_REPORT: 'BETSLIP_REMOVE_REPORT',
  BETSLIP_UPDATE_REPORTS: 'BETSLIP_UPDATE_REPORTS'
}

export function globalBetslipDispatch (action) {
  return Bus.emit('betslip:global', action)
}

export function globalBetslipStateUpdate (payload) {
  return Bus.emit('betslip:global', {
    type: GlobalBetslipActions.BETSLIP_STATE_UPDATE,
    payload
  })
}

function reportIncludesPendingBets (reports) {
  return reports?.some(bet => bet.betStatus === BET_STATUSES.PENDING_APPROVAL)
}

function globalBetslipReducer (state, action) {
  switch (action.type) {
    case GlobalBetslipActions.BETSLIP_STATE_UPDATE:
      return {
        ...state,
        ...action.payload
      }

    case GlobalBetslipActions.BETSLIP_CLEAR_REPORTS: {
      // Do not remove pending bet reports (i.e. Intercepted)
      const reports = (state.reports || []).filter(bet => bet.betStatus === BET_STATUSES.PENDING_APPROVAL)
      return {
        ...state,
        reports,
        pending: reports.length > 0
      }
    }

    case GlobalBetslipActions.BETSLIP_ADD_REPORT: {
      // todo: this shouldn't be here. Event should be triggered in each state
      // BetManager.clearBets()

      const reports = [
        ...state.reports || [],
        ...action.payload || []
      ]
      const hasPending = reportIncludesPendingBets(reports)
      return {
        ...state,
        reports,
        pending: hasPending
      }
    }

    case GlobalBetslipActions.BETSLIP_REMOVE_REPORT: {
      const reports = (state.reports || []).filter((report) => report.betId !== action.payload)
      const hasPending = reportIncludesPendingBets(reports)
      return {
        ...state,
        reports,
        pending: hasPending
      }
    }

    case GlobalBetslipActions.BETSLIP_UPDATE_REPORTS: {
      if (isEmpty(action.payload)) {
        return state
      }

      const index = state.reports.findIndex((report) => {
        return (report.betId === action.payload.betId || (action.payload.originalBets && report.betId === action.payload.originalBets[0]?.betId))
      })

      let reports = [...state.reports]

      if (index >= 0) {
        reports[index] = action.payload // replace one
      } else {
        reports = [action.payload] // replace all
      }

      const hasPending = reportIncludesPendingBets(reports)
      return {
        ...state,
        reports,
        pending: hasPending
      }
    }

    default:
      return state
  }
}

function createGlobalBetslipState () {
  const [state, dispatch] = React.useReducer(
    globalBetslipReducer,
    emptyGlobalBetslipState
  )

  React.useEffect(
    () => {
      // Invoke loading & resolving BetManager betslip settings
      BetManager.loadBetslipSettings()

      const handleBetslipDispatch = action => dispatch(action)

      /* const handleSuccessPlaceBet = ({ data }) => {
        logger.debug('[betslip:global]', 'bets placed successfully')

        dispatch({
          type: GlobalBetslipActions.BETSLIP_ADD_REPORT,
          payload: data.bets
        })
      } */

      const handleResultPlaceBet = ({ data }) => {
        logger.debug('[betslip:global]', 'bets placed result')

        if (data.bets && data.bets.length) {
          dispatch({
            type: GlobalBetslipActions.BETSLIP_ADD_REPORT,
            payload: data.bets
          })
        }
      }

      const handleInterceptResolve = ({ data }) => {
        logger.debug('[betslip:global]', 'intersepted bet resolved: ', data.resolution, data)

        let payload

        if (data.resolution === BET_INTERCEPT_STATUSES.APPROVED) {
          payload = {
            ...data.details,
            resolution: data.resolution
          }
        } else if (
          data.resolution === BET_INTERCEPT_STATUSES.PARTIALLY_APPROVED
        ) {
          payload = {
            ...data.details.updatedBet,
            originalBets: [data.details.originalBet],
            resolution: data.resolution
          }
        } else if (data.resolution === BET_INTERCEPT_STATUSES.REJECTED) {
          payload = {
            ...data.details.bet,
            updatedOdds: data.details.updatedOdds,
            rejectionReason: data.details.rejectionReason,
            resolution: data.resolution
          }
          const updatedOdds = data.details?.updatedOdds
          if (data.details.rejectionReason === REJECTION_REASON.ODDS_REDUCED && updatedOdds?.length) {
            let totalOdds = 1
            updatedOdds.forEach((outcome) => { totalOdds *= outcome.odds })
            payload.originalBets = [data.details.bet]
            payload.betOdds = totalOdds
          }
        }

        dispatch({
          type: GlobalBetslipActions.BETSLIP_UPDATE_REPORTS,
          payload
        })
      }

      const handleRemoveBetReport = ({ betId }) => dispatch({
        type: GlobalBetslipActions.BETSLIP_REMOVE_REPORT,
        payload: betId
      })

      /*  const handleFailedPlaceBet = ({ data }) => {
        const {
          placeBetRequestId,
          code,
          errors
        } = data
        // todo: need to refactor all Betslip State
      } */

      // Register dispatcher
      Bus.on('betslip:global', handleBetslipDispatch)
      // Bus.on(Bus.events.bet.placedSuccess, handleSuccessPlaceBet)
      Bus.on(Bus.events.bet.placedResult, handleResultPlaceBet)
      Bus.on(Bus.events.bet.interceptResolve, handleInterceptResolve)
      Bus.on(Bus.events.bet.removeBetReport, handleRemoveBetReport)
      // Bus.on(Bus.events.bet.placedFail, handleFailedPlaceBet)

      return () => {
        Bus.off('betslip:global', handleBetslipDispatch)
        // Bus.off(Bus.events.bet.placedSuccess, handleSuccessPlaceBet)
        Bus.off(Bus.events.bet.placedResult, handleResultPlaceBet)
        Bus.off(Bus.events.bet.interceptResolve, handleInterceptResolve)
        Bus.off(Bus.events.bet.removeBetReport, handleRemoveBetReport)
        // Bus.off(Bus.events.bet.placedFail, handleFailedPlaceBet)
      }
    },
    []
  )

  useReportsOddsHighlight(state.reports)

  return state
}

export function useGlobalBetslipState () {
  return React.useContext(GlobalBetslipContext)
}

function GlobalBetslipContextProvider (props) {
  const betslipState = createGlobalBetslipState()

  return (
    <GlobalBetslipContext.Provider value={betslipState}>
      {props.children}
    </GlobalBetslipContext.Provider>
  )
}

export function GlobalBetslipProvider (props) {
  const {
    children
  } = props

  return (
    <OddsHighlighter>
      <GlobalBetslipContextProvider>
        {children}
      </GlobalBetslipContextProvider>
    </OddsHighlighter>
  )
}
